import type { ReactNode } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Z, type ZIndex } from '../../tokens/z-index';

// ---- Root ----
export interface DialogProps extends DialogPrimitive.DialogProps {
  children: ReactNode;
}

export function Dialog({ children, ...rest }: DialogProps) {
  return <DialogPrimitive.Root {...rest}>{children}</DialogPrimitive.Root>;
}

// ---- Trigger ----
export interface DialogTriggerProps extends DialogPrimitive.DialogTriggerProps {}

export function DialogTrigger({ children, ...rest }: DialogTriggerProps) {
  return (
    <DialogPrimitive.Trigger asChild {...rest}>
      {children}
    </DialogPrimitive.Trigger>
  );
}

// ---- Content ----
export interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  /**
   * Override do tier de z-index. Default: `Z.MODAL` (400). Usar `Z.TOAST`
   * (600) quando o Dialog abre por cima de um Sheet hospedado em
   * `Z.DROPDOWN_OVER_MODAL` (500) — senão compartilha tier com o host.
   */
  zIndex?: ZIndex;
}

export function DialogContent({
  children,
  className,
  style,
  zIndex = Z.MODAL,
  ...rest
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        // Inline zIndex bypasses any Tailwind JIT cache issue with arbitrary
        // values like z-[400]. Keeps the class for parity with existing convention
        // but the inline value is authoritative.
        style={{ zIndex }}
        className={cn(
          // Z.MODAL (400) — above sidebar (200) and dropdowns (300).
          // Overlay is also the centering flex container for content.
          // Using flex instead of `left/top 50% translate` is immune to
          // ancestor transforms that would turn fixed into absolute.
          // Pedro 2026-05-22: bumped 50→70 opacity + blur-sm→md pra esconder
          // melhor UI atrás do modal (estava "competindo visualmente").
          'fixed inset-0 flex items-center justify-center bg-secondary-950/70 backdrop-blur-md p-4',
          'animate-in fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        )}
      />
      <DialogPrimitive.Content
        // Radix uses data-[state=...] on the content itself; the inset-0
        // wrapper around Content mirrors the overlay so the content
        // renders centered regardless of transformed ancestors.
        style={{ zIndex, ...style }}
        className={cn(
          // 100dvh (não 100vh): no navegador móvel `vh` mede o viewport com a
          // barra de endereço retraída, então um modal alto ultrapassa a dobra
          // visível e o rodapé dele fica inalcançável.
          'fixed inset-0 m-auto flex h-fit max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg',
          'flex-col overflow-y-auto rounded-lg border border-slate-200 bg-white p-6 shadow-lg scrollbar-subtle',
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...rest}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4 rounded-sm text-slate-400 transition-colors',
            'hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ---- Header ----
export function DialogHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-1.5', className)}>{children}</div>;
}

// ---- Title ----
export interface DialogTitleProps extends DialogPrimitive.DialogTitleProps {}

export function DialogTitle({ children, className, ...rest }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-heading font-semibold text-secondary-950', className)}
      {...rest}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

// ---- Description ----
export interface DialogDescriptionProps extends DialogPrimitive.DialogDescriptionProps {}

export function DialogDescription({ children, className, ...rest }: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      className={cn('text-sm font-body text-slate-500', className)}
      {...rest}
    >
      {children}
    </DialogPrimitive.Description>
  );
}

// ---- Footer ----
export function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mt-6 flex items-center justify-end gap-3', className)}>{children}</div>
  );
}
